import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { Container, TabContent, TabPane, Nav, NavItem, NavLink, Card, CardBody, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Breadcrumbs, Btn } from '../../../AbstractElements';
import classnames from 'classnames';
import { getDefaultTodoActiveTabId, getTodoRoleConfig, getVisibleTodoTabs } from '../../../Config/Config';
import { TodoCategoriesList } from './TodoListCategories';
import { Plus, Info, Edit } from 'react-feather';
import { todoTabInfo } from '../../../Config/TodoTabInfo';
import { fetchTableData, fetchFormData, requestNewTask } from '../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import TodoTabAllTasks from './Tabs/TodoTabAllTasks';
import TodoTabOwnTasks from './Tabs/TodoTabOwnTasks';
import TodoTabTodo from './Tabs/TodoTabTodo';
import TodoTabInProgress from './Tabs/TodoTabInProgress';
import TodoTabCompleted from './Tabs/TodoTabCompleted';
import TodoTabInactive from './Tabs/TodoTabInactive';
import TodoTabArbitrationReady from './Tabs/TodoTabArbitrationReady';
import TodoTabArbitrationActive from './Tabs/TodoTabArbitrationActive';
import TodoTabEnforcement from './Tabs/TodoTabEnforcement';
import TodoTabFinalCompensation from './Tabs/TodoTabFinalCompensation';
import TodoTabClosedFiles from './Tabs/TodoTabClosedFiles';
import TodoTabListing from './Tabs/TodoTabListing';

const TodoList = () => {
    const navigate = useNavigate();

    const [userRoleConfig, setUserRoleConfig] = useState(getTodoRoleConfig(localStorage.getItem('userRole')));
    const [visibleTabs, setVisibleTabs] = useState(getVisibleTodoTabs(TodoCategoriesList, localStorage.getItem('userRole')));
    const [canRequestTask, setCanRequestTask] = useState(userRoleConfig.canRequestTask);

    const [activeTab, setActiveTab] = useState(getDefaultTodoActiveTabId(TodoCategoriesList, localStorage.getItem('userRole')));
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [ownTasksItems, setOwnTasksItems] = useState([]);
    const [isOwnTasksLoading, setIsOwnTasksLoading] = useState(true);
    const [ownTasksTotalRows, setOwnTasksTotalRows] = useState(0);
    const [ownTasksPage, setOwnTasksPage] = useState(1);
    const [ownTasksPageSize, setOwnTasksPageSize] = useState(10);
    const [maxTaskCount, setMaxTaskCount] = useState(0);
    const [todayTaskCount, setTodayTaskCount] = useState(0);
    const [isCountLoading, setIsCountLoading] = useState(false);
    const [isAssigningTask, setIsAssigningTask] = useState(false);
    const [listRefreshKey, setListRefreshKey] = useState(0);

    const [activeListCount, setActiveListCount] = useState(0);
    const [isActiveListLoading, setIsActiveListLoading] = useState(false);

    const [appraiserTaskCount, setAppraiserTaskCount] = useState(0);
    const [poolTaskCount, setPoolTaskCount] = useState(0);
    const [isAppraiserCountLoading, setIsAppraiserCountLoading] = useState(false);

    const [arbitrationReadyCount, setArbitrationReadyCount] = useState(0);
    const [isArbitrationReadyLoading, setIsArbitrationReadyLoading] = useState(false);

    const [isArbitrationActiveLoading, setIsArbitrationActiveLoading] = useState(false);
    const [arbitrationActiveTotalRows, setArbitrationActiveTotalRows] = useState(0);

    const [isEnforcementLoading, setIsEnforcementLoading] = useState(true);
    const [enforcementTotalRows, setEnforcementTotalRows] = useState(0);

    const [isFinalCompensationLoading, setIsFinalCompensationLoading] = useState(true);
    const [finalCompensationTotalRows, setFinalCompensationTotalRows] = useState(0);

    const [isClosedFilesLoading, setIsClosedFilesLoading] = useState(true);
    const [closedFilesTotalRows, setClosedFilesTotalRows] = useState(0);

    useEffect(() => {
        const handleRoleChange = () => {
            const newUserRole = localStorage.getItem('userRole');
            const newRoleConfig = getTodoRoleConfig(newUserRole);
            const newVisibleTabs = getVisibleTodoTabs(TodoCategoriesList, newUserRole);
            const newDefaultActiveTab = getDefaultTodoActiveTabId(TodoCategoriesList, newUserRole);

            setUserRoleConfig(newRoleConfig);
            setVisibleTabs(newVisibleTabs);
            setCanRequestTask(newRoleConfig.canRequestTask);

            setActiveTab(newDefaultActiveTab);
            setActiveListCount(0);
            setIsActiveListLoading(true);

            setOwnTasksPage(1);
            setArbitrationReadyCount(0);
            setIsArbitrationReadyLoading(true);
            setArbitrationActiveTotalRows(0);
            setIsArbitrationActiveLoading(true);
            setListRefreshKey(prev => prev + 1);
        };

        window.addEventListener('userRoleChanged', handleRoleChange);

        return () => {
            window.removeEventListener('userRoleChanged', handleRoleChange);
        };
    }, []);

    const dataPoolColumns = [
        { name: 'Plaka', selector: row => row.plate, sortable: true },
        { name: 'Şase Numarası', selector: row => row.chassisNumber, sortable: true },
        { name: 'Kaza Tarihi', selector: row => row.accidentDate ? new Date(row.accidentDate).toLocaleString() : '-', sortable: true },
        { name: 'Rapor Tarihi', selector: row => row.reportDate ? new Date(row.reportDate).toLocaleString() : '-', sortable: true },
        { name: 'TC Kimlik Numarası', selector: row => row.tcid, sortable: true },
        { name: 'Ad Soyad', selector: row => row.fullName, sortable: true },
        { name: 'Kaynak Klasör', selector: row => row.originFolder || '-', sortable: true },
        {
            name: 'Telefon', selector: row => {
                if (Array.isArray(row.phone) && row.phone.length > 0) {
                    const firstPhoneObj = row.phone[0];
                    if (firstPhoneObj && typeof firstPhoneObj === 'object' && firstPhoneObj.hasOwnProperty('phone') && firstPhoneObj.phone) {
                        const firstPhone = firstPhoneObj.phone;
                        if (row.phone.length > 1) {
                            const validPhonesCount = row.phone.filter(p => p && p.phone && p.phone.trim() !== '').length;
                            if (validPhonesCount > 1) {
                                const remainingCount = validPhonesCount - 1;
                                return `${firstPhone} +${remainingCount}`;
                            }
                        }
                        return firstPhone;
                    }
                }
                return '-';
            }, sortable: true
        },
        { name: 'Kusur Durumu', selector: row => (row.faultStatus !== null && row.faultStatus !== undefined ? `${row.faultStatus}%` : '-'), sortable: true },
        {
            name: 'Çağrı Formu',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'primary',
                        size: 'sm',
                        outline: true,
                        onClick: () => {
                            if (row.id) {
                                navigate(`/callform/new-callform/${row.id}`);
                            } else {
                                toast.error("Bu kayıt için form oluşturulamıyor (ID eksik).");
                            }
                        }
                    }}
                >
                    <Edit size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            right: true,
        }
    ];

    const handleListCountChange = useCallback((count) => {
        setActiveListCount(count);
        setIsActiveListLoading(false);
    }, []);

    const toggle = tab => {
        const targetCategory = TodoCategoriesList[parseInt(tab, 10) - 1];
        const isVisible = visibleTabs.some(cat => cat.title === targetCategory?.title);
        if (isVisible && activeTab !== tab) {
            setActiveTab(tab);
            setActiveListCount(0);
            setIsActiveListLoading(true);
            if (tab === '2') {
                setOwnTasksPage(1);
            }
            if (tab === '7') {
                setArbitrationReadyCount(0);
                setIsArbitrationReadyLoading(true);
            }
            if (tab === '8') {
                setArbitrationActiveTotalRows(0);
                setIsArbitrationActiveLoading(true);
            }
        }
    };

    const toggleInfo = () => setIsInfoOpen(!isInfoOpen);

    const fetchOwnTasks = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/datapool/get-own';
        if (!apiEndpoint) {
            toast.error("Own tasks API endpoint is not configured.");
            return;
        }
        setIsOwnTasksLoading(true);
        try {
            await fetchTableData({
                apiEndpoint: apiEndpoint,
                page: ownTasksPage,
                pageSize: ownTasksPageSize,
                search: '',
                setDataCallback: setOwnTasksItems,
                setTotalRowsCallback: setOwnTasksTotalRows,
            });
        } catch (error) {
            setOwnTasksTotalRows(0);
        } finally {
            setIsOwnTasksLoading(false);
        }
    }, [ownTasksPage, ownTasksPageSize]);

    const fetchTaskCounts = useCallback(async () => {
        if (!canRequestTask) return;
        const countApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/datapool/task-count';
        if (!countApiEndpoint) {
            return;
        }
        setIsCountLoading(true);
        try {
            const response = await fetchFormData({ apiEndpoint: countApiEndpoint });
            let taskData = null;
            if (response && response.isSuccess && response.data) {
                taskData = response.data;
            } else if (response && response.hasOwnProperty('maxTaskCount') && response.hasOwnProperty('todayTaskCount')) {
                taskData = response;
            }
            if (taskData) {
                setMaxTaskCount(taskData.maxTaskCount ?? 0);
                setTodayTaskCount(taskData.todayTaskCount ?? 0);
            } else {
                toast.error("Görev sayıları alınamadı veya yanıt formatı geçersiz.");
            }
        } catch (error) {
            toast.error("Görev sayıları alınırken bir hata oluştu.");
        } finally {
            setIsCountLoading(false);
        }
    }, [canRequestTask]);

    const fetchAppraiserTaskCounts = useCallback(async () => {
        if (!canRequestTask || (userRoleConfig.taskType !== 'appraiser' && userRoleConfig.taskType !== 'headAppraiser')) return;

        const countApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callform/task-count';
        if (!countApiEndpoint) {
            return;
        }
        setIsAppraiserCountLoading(true);
        try {
            const response = await fetchFormData({ apiEndpoint: countApiEndpoint });
            let taskData = null;
            if (response && response.isSuccess && response.data) {
                taskData = response.data;
            } else if (response && response.hasOwnProperty('myTaskCount') && response.hasOwnProperty('totalTaskCount')) {
                taskData = response;
            }
            if (taskData) {
                setAppraiserTaskCount(taskData.myTaskCount ?? 0);
                setPoolTaskCount(taskData.totalTaskCount ?? 0);
            } else {
                toast.error("Görev sayıları alınamadı veya yanıt formatı geçersiz.");
            }
        } catch (error) {
            toast.error("Görev sayıları alınırken bir hata oluştu.");
        } finally {
            setIsAppraiserCountLoading(false);
        }
    }, [canRequestTask, userRoleConfig.taskType]);

    useEffect(() => {
        const ownTasksTabIndex = 1;
        const ownTasksTabVisible = visibleTabs.some((_category, index) => index === ownTasksTabIndex);
        if (ownTasksTabVisible && activeTab === (ownTasksTabIndex + 1).toString()) {
            fetchOwnTasks();
        }
    }, [activeTab, fetchOwnTasks, visibleTabs]);

    useEffect(() => {
        if (canRequestTask) {
            if (userRoleConfig.taskType === 'appraiser' || userRoleConfig.taskType === 'headAppraiser') {
                fetchAppraiserTaskCounts();
            } else {
                fetchTaskCounts();
            }
        }
    }, [fetchTaskCounts, fetchAppraiserTaskCounts, canRequestTask, userRoleConfig.taskType]);

    const handleRequestNewJobClick = useCallback(async () => {
        if (isAssigningTask) return;

        if (userRoleConfig.taskType === 'appraiser' || userRoleConfig.taskType === 'headAppraiser') {
            const assignApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callform/assign-task';
            if (!assignApiEndpoint) {
                toast.error("Assign task API endpoint is not configured.");
                return;
            }
            setIsAssigningTask(true);
            try {
                const responseData = await requestNewTask({ apiEndpoint: assignApiEndpoint });
                if (responseData && responseData.isSuccess !== false) {
                    toast.success(responseData.message || "Yeni iş başarıyla atandı!");
                    await fetchAppraiserTaskCounts();
                    await fetchOwnTasks();
                    setListRefreshKey(prevKey => prevKey + 1);
                } else {
                    const defaultErrorMessage = "İş atama sırasında bilinmeyen bir hata oluştu.";
                    const specificMessage = "Atanabilecek görev bulunamadı.";
                    if (responseData && responseData.isSuccess === false && responseData.message === specificMessage) {
                        toast.warn(specificMessage);
                    } else {
                        toast.error(responseData?.message || defaultErrorMessage);
                    }
                }
            } catch (error) {
            } finally {
                setIsAssigningTask(false);
            }
            return;
        }

        if (userRoleConfig.taskType === 'callCenter' || userRoleConfig.taskType === 'headCallCenter') {
            if (todayTaskCount >= maxTaskCount && maxTaskCount > 0) {
                toast.warn("Günlük maksimum iş talep sayısına ulaştınız.");
                return;
            }
            const assignApiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/datapool/assign-task';
            if (!assignApiEndpoint) {
                toast.error("Assign task API endpoint is not configured.");
                return;
            }
            setIsAssigningTask(true);
            try {
                const responseData = await requestNewTask({ apiEndpoint: assignApiEndpoint });
                if (responseData && responseData.isSuccess !== false) {
                    toast.success(responseData.message || "Yeni iş başarıyla atandı!");
                    await fetchTaskCounts();
                    await fetchOwnTasks();
                    setListRefreshKey(prevKey => prevKey + 1);
                } else {
                    const defaultErrorMessage = "İş atama sırasında bilinmeyen bir hata oluştu.";
                    const specificMessage = "Atanabilecek görev bulunamadı.";
                    if (responseData && responseData.isSuccess === false && responseData.message === specificMessage) {
                        toast.warn(specificMessage);
                    } else {
                        toast.error(responseData?.message || defaultErrorMessage);
                    }
                }
            } catch (error) {
            } finally {
                setIsAssigningTask(false);
            }
        }
    }, [isAssigningTask, todayTaskCount, maxTaskCount, fetchTaskCounts, fetchOwnTasks, fetchAppraiserTaskCounts, userRoleConfig.taskType]);

    const handleOwnTasksPageChange = (newPage) => setOwnTasksPage(newPage);
    const handleOwnTasksPerPageChange = (newPerPage, newPage) => {
        setOwnTasksPageSize(newPerPage);
        setOwnTasksPage(newPage);
    };

    const assignButtonDisabled = ['appraiser', 'headAppraiser'].includes(userRoleConfig.taskType)
        ? isAssigningTask
        : (isAssigningTask || (todayTaskCount >= maxTaskCount && maxTaskCount > 0));

    const assignButtonTitle = ['appraiser', 'headAppraiser'].includes(userRoleConfig.taskType)
        ? (isAssigningTask ? "İş atanıyor..." : "Yeni İş Talep Et")
        : (todayTaskCount >= maxTaskCount && maxTaskCount > 0)
            ? "Günlük limit doldu"
            : (isAssigningTask ? "İş atanıyor..." : "Yeni İş Talep Et");

    return (
        <Fragment>
            <Breadcrumbs parent='Sayfalar' title='İş Listesi' />
            <Container fluid>
                <Card>
                    <div className="d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center w-100 p-3 p-md-4 gap-lg-5">
                        <Nav className='nav-primary nav-pills d-flex flex-wrap mb-4 mb-lg-0 flex-grow-1 justify-content-center'>
                            {visibleTabs.map((category) => {
                                const categoryIndex = TodoCategoriesList.indexOf(category);
                                const tabId = (categoryIndex + 1).toString();
                                const isActive = activeTab === tabId;

                                let count = '-';
                                let isLoading = false;

                                if (isActive) {
                                    if (tabId === '2') {
                                        isLoading = isOwnTasksLoading;
                                        count = ownTasksTotalRows;
                                    } else if (tabId === '7') {
                                        isLoading = isArbitrationReadyLoading;
                                        count = arbitrationReadyCount;
                                    } else if (tabId === '8') {
                                        isLoading = isArbitrationActiveLoading;
                                        count = arbitrationActiveTotalRows;
                                    } else if (tabId === '9') {
                                        isLoading = isEnforcementLoading;
                                        count = enforcementTotalRows;
                                    } else if (tabId === '10') {
                                        isLoading = isFinalCompensationLoading;
                                        count = finalCompensationTotalRows;
                                    } else if (tabId === '11') {
                                        isLoading = isClosedFilesLoading;
                                        count = closedFilesTotalRows;
                                    } else {
                                        isLoading = isActiveListLoading;
                                        count = activeListCount;
                                    }
                                }

                                if (isActive && isLoading) {
                                    count = '...';
                                }

                                return (
                                    <NavItem key={categoryIndex} className="mb-2 mb-md-0 me-2 my-2" style={{ width: '220px' }}>
                                        <NavLink
                                            role="button" tabIndex="0"
                                            className={classnames('d-flex align-items-center px-3 py-2 h-100', { 'active': isActive, 'bg-light': !isActive, 'text-dark': !isActive })}
                                            onClick={() => { toggle(tabId); }}
                                            style={{ borderRadius: '0.375rem' }}
                                        >
                                            <div className="d-flex align-items-center w-100 justify-content-between">
                                                <div className="d-flex align-items-center">
                                                    <span className={`d-inline-flex align-items-center ${isActive ? 'text-white' : `text-${category.color}`}`}>
                                                        {category.icon}
                                                    </span>
                                                    <span className="ms-2">{category.title}</span>
                                                </div>
                                                <span
                                                    className={`badge ${isActive ? 'bg-white text-primary' : `bg-${category.color}`} rounded-circle d-inline-flex align-items-center justify-content-center`}
                                                    style={{ minWidth: '24px', height: '24px', fontSize: count === '...' || count === '-' ? '0.7em' : '1em', padding: '0 5px' }}
                                                >
                                                    {count}
                                                </span>
                                            </div>
                                        </NavLink>
                                    </NavItem>
                                );
                            })}
                        </Nav>

                        {canRequestTask && (
                            <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-3 mt-3 mt-lg-0 flex-shrink-0">
                                <div className="d-flex gap-3 justify-content-center text-center text-lg-start">
                                    {(userRoleConfig.taskType === 'appraiser' || userRoleConfig.taskType === 'headAppraiser') ? (
                                        isAppraiserCountLoading ? (
                                            <small className="text-muted">Yükleniyor...</small>
                                        ) : (
                                            <>
                                                <div className='border p-2 rounded bg-light'>
                                                    <small className="text-muted d-block">Atanan İş</small>
                                                    <span className="fw-bold fs-6 text-dark">{appraiserTaskCount}</span>
                                                </div>
                                                <div className='border p-2 rounded bg-light'>
                                                    <small className="text-muted d-block">Havuzdaki İş</small>
                                                    <span className="fw-bold fs-6 text-dark">{poolTaskCount}</span>
                                                </div>
                                            </>
                                        )
                                    ) : (
                                        isCountLoading ? (
                                            <small className="text-muted">Yükleniyor...</small>
                                        ) : (
                                            <>
                                                <div className='border p-2 rounded bg-light'>
                                                    <small className="text-muted d-block">Bugün Alınan</small>
                                                    <span className="fw-bold fs-6 text-dark">{todayTaskCount}</span>
                                                </div>
                                                <div className='border p-2 rounded bg-light'>
                                                    <small className="text-muted d-block">Maks. Talep</small>
                                                    <span className="fw-bold fs-6 text-dark">{maxTaskCount}</span>
                                                </div>
                                            </>
                                        )
                                    )}
                                </div>
                                <div className="d-flex flex-column align-items-stretch gap-2">
                                    <Btn attrBtn={{
                                        color: 'outline-primary d-flex align-items-center justify-content-center',
                                        onClick: handleRequestNewJobClick,
                                        style: { backgroundColor: '#9188f521', border: '1px solid #9188f540' },
                                        disabled: assignButtonDisabled,
                                        title: assignButtonTitle
                                    }}>
                                        {isAssigningTask ? (<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>) : (<Plus style={{ width: '20px', height: '20px' }} className='me-2' />)}
                                        {assignButtonTitle}
                                    </Btn>
                                    <Btn attrBtn={{ color: 'outline-info d-flex align-items-center justify-content-center', onClick: toggleInfo, style: { backgroundColor: '#57daff1f', border: '1px solid #57daff40' }, }}>
                                        <Info style={{ width: '20px', height: '20px' }} className='me-2' /> Bilgi
                                    </Btn>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
                <Card>
                    <CardBody>
                        <TabContent activeTab={activeTab}>
                            {visibleTabs.map((category) => {
                                const categoryIndex = TodoCategoriesList.indexOf(category);
                                const tabId = (categoryIndex + 1).toString();

                                if (tabId === '1') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabAllTasks
                                                    key={`tab-1-${listRefreshKey}`}
                                                    onCountChange={handleListCountChange}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '2') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabOwnTasks
                                                    columns={dataPoolColumns}
                                                    items={ownTasksItems}
                                                    loading={isOwnTasksLoading}
                                                    totalRows={ownTasksTotalRows}
                                                    page={ownTasksPage}
                                                    pageSize={ownTasksPageSize}
                                                    onChangePage={handleOwnTasksPageChange}
                                                    onChangeRowsPerPage={handleOwnTasksPerPageChange}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '3') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabTodo
                                                    key={`tab-3-${listRefreshKey}`}
                                                    onCountChange={handleListCountChange}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '4') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabInProgress
                                                    key={`tab-4-${listRefreshKey}`}
                                                    onCountChange={handleListCountChange}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '5') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabCompleted
                                                    key={`tab-5-${listRefreshKey}`}
                                                    onCountChange={handleListCountChange}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '6') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabInactive
                                                    key={`tab-6-${listRefreshKey}`}
                                                    onCountChange={handleListCountChange}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '7') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabArbitrationReady
                                                    onCountChange={setArbitrationReadyCount}
                                                    onLoadingChange={setIsArbitrationReadyLoading}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '8') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabArbitrationActive
                                                    onCountChange={setArbitrationActiveTotalRows}
                                                    onLoadingChange={setIsArbitrationActiveLoading}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '9') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabEnforcement
                                                    onCountChange={setEnforcementTotalRows}
                                                    onLoadingChange={setIsEnforcementLoading}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '10') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabFinalCompensation
                                                    onCountChange={setFinalCompensationTotalRows}
                                                    onLoadingChange={setIsFinalCompensationLoading}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                if (tabId === '11') {
                                    return (
                                        <TabPane tabId={tabId} key={categoryIndex}>
                                            {activeTab === tabId && (
                                                <TodoTabClosedFiles
                                                    onCountChange={setClosedFilesTotalRows}
                                                    onLoadingChange={setIsClosedFilesLoading}
                                                />
                                            )}
                                        </TabPane>
                                    );
                                }

                                return (
                                    <TabPane tabId={tabId} key={categoryIndex}>
                                        {activeTab === tabId && (
                                            <TodoTabListing
                                                tabId={tabId}
                                                listRefreshKey={listRefreshKey}
                                                onCountChange={handleListCountChange}
                                            />
                                        )}
                                    </TabPane>
                                );
                            })}
                        </TabContent>
                    </CardBody>
                </Card>
                <Modal isOpen={isInfoOpen} toggle={toggleInfo}>
                    <ModalHeader toggle={toggleInfo}>Sekme Bilgileri</ModalHeader>
                    <ModalBody>
                        {Object.keys(todoTabInfo).map(key => (
                            <div key={key} className="mb-3">
                                <h6 style={{ color: todoTabInfo[key].color, fontWeight: 'bold' }}>{todoTabInfo[key].title}</h6>
                                <p className="mb-0">{todoTabInfo[key].content}</p>
                            </div>
                        ))}
                    </ModalBody>
                    <ModalFooter>
                        <Btn attrBtn={{ color: 'secondary', onClick: toggleInfo }}>Kapat</Btn>
                    </ModalFooter>
                </Modal>

            </Container>
        </Fragment>
    );
};

export default TodoList;

